import React, { useState } from "react";
import { useOne } from "@pankod/refine-core";
import {
    useModalForm, Modal, Space, Form,
    Button,
    Input,
    Select,
    Typography,
    Alert,
    Spin,
} from "@pankod/refine-antd";
import { DeleteOutlined } from '@ant-design/icons';
import { IUser } from "../../../../interfaces/company";

const { TextArea } = Input;
const { Text } = Typography;

interface RemoveUacmModelProps {
    refetchUsers: any;
    refetchCompanyDetail: any;
    userItem: IUser;
    companyId: string;
}

export const RemoveUacmModel: React.FC<RemoveUacmModelProps> = ({
    refetchUsers,
    refetchCompanyDetail,
    userItem,
    companyId,
}) => {
    let uacmId = userItem.uacmId;
    const [isOprDataLoaded, setIsOprDataLoaded] = useState(false);

    // Fetch pending OPR count for the UACM
    const { refetch: refetchOprCount, data: oprData, isLoading: oprLoading } = useOne<any>({
        dataProviderName: "grapghQl",
        resource: 'totalOprRequestPendingOfUacm',
        metaData: {
            uacmId: uacmId,
        },
        id: '',
        queryOptions: {
            enabled: false,
            onSuccess: () => {
                setIsOprDataLoaded(true);
            },
        },
    });

    // Fetch company admin users for the reassignment dropdown
    const { refetch: refetchAdminUsers, data: adminUsersData, isLoading: adminUsersLoading } = useOne<any>({
        dataProviderName: "grapghQl",
        resource: 'getCompanyAdminDetail',
        metaData: {
            companyId: companyId,
            userAccountToExclude: [userItem.id]
        },
        id: '',
        queryOptions: {
            enabled: false,
        },
    });

    const approvalOprCount = oprData?.data?.approvalOprCount || 0;
    const checkerOprCount = oprData?.data?.checkerOprCount || 0;
    const totalOprCount = approvalOprCount + checkerOprCount;
    const canDeleteWithoutReassignment = oprData?.data?.permission?.canUacmBeDeletedWithoutOprReassigment || false;
    const doesUserHaveActiveReassignment = oprData?.data?.permission?.doesUserHaveActiveReassignment || false;
    const needsReassignment = totalOprCount > 0 && !canDeleteWithoutReassignment;

    // Build reassignment options from admin users data, filtering out the current user
    const reassignmentOptions = (adminUsersData?.data || [])
        .map((adminUser: any) => {
            const adminUacm = adminUser?.userAccounts
                ?.flatMap((ua: any) => ua?.companyMappingList || [])
                ?.find((mapping: any) => mapping?.companyId === companyId);
            return {
                label: `${adminUser.displayName || adminUser.fullName} (${adminUser.emailId})`,
                value: adminUacm?.id,
            };
        });

    // Remove UACM modal form
    const {
        modalProps: removeUacmModal,
        formProps: removeUacmForm,
        show: removeUacmShow,
        close: removeUacmClose
    } = useModalForm<any>({
        dataProviderName: "grapghQl",
        resource: "removeAccountCompanyMappingViaInternalPortal",
        action: "create",
        metaData: {
            removalUacmId: uacmId,
            companyId: companyId,
        },
        redirect: false,
        onMutationSuccess() {
            refetchCompanyDetail(true);
            refetchUsers(true);
            removeUacmClose();
        },
    });

    removeUacmModal.title = `Remove User From Company`;
    removeUacmModal.centered = true;
    removeUacmModal.width = 600;
    removeUacmModal.okText = 'Confirm Removal';
    removeUacmModal.onCancel = () => {
        removeUacmForm.form?.resetFields();
        removeUacmClose()
        uacmId = '';
    }

    const handleOpenModal = () => {
        setIsOprDataLoaded(false);
        removeUacmShow();
        refetchOprCount();
        refetchAdminUsers();
    };

    return (
        <Space>
            <Button
                danger={true}
                onClick={handleOpenModal}
                icon={<DeleteOutlined />}
            >
                Remove From Company
            </Button>

            <Modal {...removeUacmModal}>
                <h4>Remove {userItem.fullName || userItem.displayName} from this company?</h4>

                {(oprLoading || adminUsersLoading) && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin tip="Checking pending OPR requests..." />
                    </div>
                )}

                {isOprDataLoaded && !oprLoading && (
                    <>
                        {doesUserHaveActiveReassignment && (
                            <Alert
                                type="warning"
                                showIcon
                                message="Active Reassignment In Progress"
                                description="This user already has an active OPR reassignment in progress. Please wait for it to complete before attempting removal."
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {totalOprCount > 0 && (
                            <Alert
                                type="info"
                                showIcon
                                message="Pending OPR Requests"
                                description={
                                    <div>
                                        <Text>Approval OPR Count: <Text strong>{approvalOprCount}</Text></Text>
                                        <br />
                                        <Text>Checker OPR Count: <Text strong>{checkerOprCount}</Text></Text>
                                    </div>
                                }
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {totalOprCount === 0 && (
                            <Alert
                                type="success"
                                showIcon
                                message="No Pending OPR Requests"
                                description="This user has no pending OPR requests. You can proceed with removal."
                                style={{ marginBottom: 16 }}
                            />
                        )}
                    </>
                )}

                <Form {...removeUacmForm} layout="vertical">
                    {isOprDataLoaded && needsReassignment && !doesUserHaveActiveReassignment && (
                        <Form.Item
                            label="Reassign OPR Requests To"
                            name="reassignmentUacmId"
                            rules={[
                                {
                                    message: 'Please select a user to reassign OPR requests to',
                                    required: true,
                                },
                            ]}
                        >
                            <Select
                                showSearch={true}
                                placeholder="Select a user for OPR reassignment"
                                options={reassignmentOptions}
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        label="Notes"
                        name="notes"
                        rules={[
                            {
                                message: 'Notes are required',
                                required: true,
                            },
                        ]}
                    >
                        <TextArea rows={4} placeholder="Reason for removing user from company" />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};
